/**
 * useMainAppArchivedOrgs
 *
 * Fetches archived organizations (status = 'archived' or 'archive_pending')
 * from the main app's Supabase instance using the service role key.
 *
 * Returns each org with its archived location count for display in the
 * Archived Organizations section of the ops portal.
 */

import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

export interface ArchivedLocation {
  id: string;
  name: string;
  logo_url: string | null;
  description: string | null;
  archived_at: string | null;
}

export interface ArchivedOrg {
  id: string;
  name: string;
  status: 'archive_pending' | 'archived';
  archived_at: string | null;
  logo_url: string | null;
  archived_location_count: number;
  archived_locations: ArchivedLocation[];
}

export function useMainAppArchivedOrgs() {
  const [orgs, setOrgs] = useState<ArchivedOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchArchivedOrgs = async () => {
    try {
      setLoading(true);
      setError(null);

      const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
      const serviceRoleKey = import.meta.env.VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

      if (!mainAppUrl || !serviceRoleKey) {
        throw new Error('Main app Supabase credentials not configured');
      }

      const mainApp = createClient(mainAppUrl, serviceRoleKey, {
        auth: { autoRefreshToken: false, persistSession: false },
      });

      // Fetch archived/archive_pending orgs from the main app
      const { data: archivedOrgs, error: orgsError } = await mainApp
        .from('organizations')
        .select('id, name, status, archived_at, logo_url')
        .in('status', ['archived', 'archive_pending'])
        .order('archived_at', { ascending: false });

      if (orgsError) throw orgsError;

      if (!archivedOrgs || archivedOrgs.length === 0) {
        setOrgs([]);
        return;
      }

      // Fetch archived location details per org
      const orgIds = archivedOrgs.map((o: any) => o.id);
      const { data: archivedLocations, error: locError } = await mainApp
        .from('locations')
        .select('id, name, logo_url, description, organization_id, archived_at')
        .in('organization_id', orgIds)
        .eq('is_grouped_location', false)
        .is('deleted_at', null);

      if (locError) throw locError;

      const locsByOrg: Record<string, ArchivedLocation[]> = {};
      for (const loc of archivedLocations ?? []) {
        if (!locsByOrg[loc.organization_id]) locsByOrg[loc.organization_id] = [];
        locsByOrg[loc.organization_id].push({
          id: loc.id,
          name: loc.name,
          logo_url: loc.logo_url,
          description: loc.description,
          archived_at: loc.archived_at,
        });
      }

      const result: ArchivedOrg[] = archivedOrgs.map((o: any) => ({
        id: o.id,
        name: o.name,
        status: o.status,
        archived_at: o.archived_at,
        logo_url: o.logo_url,
        archived_location_count: locsByOrg[o.id]?.length ?? 0,
        archived_locations: locsByOrg[o.id] ?? [],
      }));

      setOrgs(result);
    } catch (err) {
      console.error('useMainAppArchivedOrgs error:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedOrgs();
  }, []);

  return { orgs, loading, error, refetch: fetchArchivedOrgs };
}
